import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  ConnectionLineType,
} from "@xyflow/react";

import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";

import "@xyflow/react/dist/style.css";

import useViewportDimensions, {
  DOCUMENT_PADDING,
  SecondaryHeightLayout,
} from "hooks/viewport-dimensions";
import {
  edgeTypes,
  getOrgLayout,
  isNodeCorrelationError,
  nodeTypes,
  DeviceNodeData,
  SourceNodeData,
  OrgChartNodeType,
  DistributionNodeData,
  ZoneNodeData,
  DeviceNodeDevice,
  ZoneLeafNode,
  DeviceNodeDataInfo,
} from "./elements/flow-utils";
import GenericButton from "widgets/controls/buttons/generic";
import GenericCard from "widgets/cards/generic";
import { deviceList } from "api/device";
import { Device, DeviceListReqBody } from "api/types/device";
import { SearchInput } from "widgets/shared/inputs/search";
import { useTranslation } from "react-i18next";
import {
  MdAdd,
  MdAlignHorizontalLeft,
  MdAlignVerticalTop,
  MdClose,
  MdDragIndicator,
  MdLocationOn,
} from "react-icons/md";
import CircleButton from "widgets/controls/buttons/circle-button";
import { AssignedDeviceCard } from "./components/assigned-device-card";
import { ModelFilterDropdown } from "models/dropdowns/modelFilter";
import {
  ReticulationCreateReqBody,
  ReticulationItem,
  ReticulationUpdateReqBody,
} from "api/types/reticulation";
import {
  addReticulation,
  getReticulation,
  updateReticulation,
} from "api/reticulation";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import { notifySimple } from "providers/toast";
import { useBackBlocker } from "providers/back-blocker-provider";
import SitesCombobox from "widgets/shared/inputs/usersCombobox";

const genId = () => `_${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const nodeWidth = 350;
const nodeHeight = 120;
const clusterWidth = nodeWidth;
const clusterHeight = nodeHeight * 3;

const COLUMN_IDS = {
  SEARCH: "search",
  SOURCE: "source",
  DISTRIBUTION: "distribution",
  ZONE: "zone",
  DEVICE: "device",
} as const;

type SourceAssignment = { device: Device };
type DistributionItem = { id: string; name: string };
type ZoneItem = { id: string; name: string; devices: { device: Device }[] };
type DeviceAssignment = { device: Device };

type ColumnAssignments = {
  source: SourceAssignment[];
  distribution: DistributionItem[];
  zone: ZoneItem[];
  device: DeviceAssignment[];
};

/** Database-ready flow node shape */
export type FlowNodeDb = {
  id: string;
  name: string;
  label: string;
  targets: string[];
  type: OrgChartNodeType;
  leafNodes?: ZoneLeafNode[];
  device?: DeviceNodeDevice;
  /** Zone-level usage / stats when provided by config or API */
  data?: DeviceNodeDataInfo;
};

export type FlowDbFormat = { nodes: FlowNodeDb[]; sites: string[] };

function nodesListToDbFormat(
  nodesList: Array<
    SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData
  >
): FlowDbFormat {
  const classIds: string[] = [];
  for (const node of nodesList) {
    if ("device" in node && node.device?.classId) {
      classIds.push(node.device.classId);
    }
    if ("leafNodes" in node && node.leafNodes?.length) {
      for (const leaf of node.leafNodes) {
        if (leaf.device?.classId) classIds.push(leaf.device.classId);
      }
    }
  }
  const sites = [...new Set(classIds)];

  const nodes = nodesList.map((node) => {
    const base = {
      id: node.id,
      name: node.name,
      label: node.name,
      targets: "targets" in node ? node.targets : [],
      type: node.type,
    };
    if ("leafNodes" in node && node.leafNodes?.length) {
      const z = node as ZoneNodeData;
      return {
        ...base,
        leafNodes: node.leafNodes,
        ...(z.data ? { data: z.data } : {}),
      };
    }
    if ("device" in node && node.device) {
      return { ...base, device: node.device };
    }
    return base;
  });

  return { nodes, sites };
}

function dbFormatToNodesList(
  dbNodes: FlowNodeDb[]
): Array<
  SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData
> {
  return dbNodes.map((n) => {
    switch (n.type) {
      case OrgChartNodeType.SOURCE:
        return new SourceNodeData(
          n.id,
          n.targets,
          n.name,
          OrgChartNodeType.SOURCE,
          n.device
        );
      case OrgChartNodeType.DISTRIBUTION:
        return new DistributionNodeData(
          n.id,
          n.targets,
          n.name,
          OrgChartNodeType.DISTRIBUTION
        );
      case OrgChartNodeType.ZONE:
        return new ZoneNodeData(
          n.id,
          n.targets,
          n.name,
          OrgChartNodeType.ZONE,
          n.leafNodes ?? [],
          undefined,
          undefined,
          undefined,
          undefined,
          n.data
        );
      case OrgChartNodeType.DEVICE:
        return new DeviceNodeData(
          n.id,
          n.name,
          OrgChartNodeType.DEVICE,
          n.device,
          undefined,
          n.targets ?? []
        );
      default:
        return new DeviceNodeData(
          n.id,
          n.name,
          OrgChartNodeType.DEVICE,
          undefined,
          undefined,
          n.targets ?? []
        );
    }
  });
}

/** Convert DeviceNodeDevice-like shape to Device for column UI */
function toDevice(d: {
  id?: string;
  snr: string;
  name: string;
  model?: string;
  classId?: string;
  timezone?: number;
}): Device {
  return {
    id: d.id ?? d.snr,
    snr: d.snr,
    name: d.name ?? "",
    model: d.model ?? "",
    classes: [],
    classId: d.classId ?? "",
    timezone: d.timezone,
    deployed: false,
    lastComms: "",
    created: "",
    host: "",
    dataQuality: "",
    recipients: [],
  };
}

function dbFormatToColumnAssignments(dbNodes: FlowNodeDb[]): ColumnAssignments {
  const source: SourceAssignment[] = [];
  const distribution: DistributionItem[] = [];
  const zone: ZoneItem[] = [];
  const device: DeviceAssignment[] = [];

  for (const n of dbNodes) {
    switch (n.type) {
      case OrgChartNodeType.SOURCE:
        if (n.device) source.push({ device: toDevice(n.device) });
        break;
      case OrgChartNodeType.DISTRIBUTION:
        distribution.push({ id: n.id, name: n.name });
        break;
      case OrgChartNodeType.ZONE:
        zone.push({
          id: n.id,
          name: n.name,
          devices: (n.leafNodes ?? []).map((l) => ({
            device: toDevice(l.device),
          })),
        });
        break;
      case OrgChartNodeType.DEVICE:
        if (n.device) device.push({ device: toDevice(n.device) });
        break;
    }
  }

  return { source, distribution, zone, device };
}

/** Seed nodeIdMapRef so Save preserves existing UUIDs when editing loaded flow */
function seedNodeIdMap(map: Map<string, string>, dbNodes: FlowNodeDb[]): void {
  for (const n of dbNodes) {
    switch (n.type) {
      case OrgChartNodeType.SOURCE:
        if (n.device) map.set(`src:${n.device.snr}`, n.id);
        break;
      case OrgChartNodeType.DISTRIBUTION:
        map.set(`dist:${n.id}`, n.id);
        break;
      case OrgChartNodeType.ZONE:
        map.set(`zone:${n.id}`, n.id);
        break;
      case OrgChartNodeType.DEVICE:
        if (n.device) map.set(`dev:${n.device.snr}`, n.id);
        break;
    }
  }
}

/**
 * Paste your flow log (FlowNodeDb[]) here to load as initial data, as if from backend.
 * Set to `undefined` or `[]` to start empty.
 */
const INITIAL_FLOW_FROM_BACKEND: FlowNodeDb[] | undefined = undefined;

const Flow = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const searchPanelRef = useRef<HTMLDivElement>(null);
  const { dimensions } = useViewportDimensions();

  const [searchParams] = useSearchParams();
  // Extract the filter parameter
  const id = searchParams.get("id");

  // Device search
  const [showNodePopup, setShowNodePopup] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDevices, setSearchDevices] = useState<Device[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [freshKey, setFreshKey] = useState(0);

  const [reticulation, setReticulation] = useState<
    ReticulationItem | undefined
  >(undefined);

  // Column assignments: device id -> column (only one column per device)
  const [columnAssignments, setColumnAssignments] = useState<ColumnAssignments>(
    {
      source: [],
      distribution: [],
      zone: [],
      device: [],
    }
  );

  const [nodesList, setNodesList] = useState<
    Array<SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData>
  >([]);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [direction, setDirection] = useState<"TB" | "LR">("LR");
  const nodeIdMapRef = useRef<Map<string, string>>(new Map());
  const reactFlowInstanceRef = useRef<{
    fitView: (options?: { padding?: number; duration?: number }) => void;
  } | null>(null);
  const initialFlowRef = useRef<FlowDbFormat | null>(null);
  const [saveRevision, setSaveRevision] = useState(0);

  const fetchFlow = useCallback(async () => {
    if (!id) return;
    dispatch(setLoading(true));
    const { data, status, isLoading } = await getReticulation(id);
    dispatch(setLoading(isLoading));
    if (status === 200 && data) {
      setReticulation(data);
      const nodes = data.config as FlowNodeDb[] | undefined;
      if (nodes?.length) {
        setNodesList(dbFormatToNodesList(nodes));
        setColumnAssignments(dbFormatToColumnAssignments(nodes));
        seedNodeIdMap(nodeIdMapRef.current, nodes);
      }
      initialFlowRef.current = nodes?.length
        ? nodesListToDbFormat(dbFormatToNodesList(nodes))
        : { nodes: [], sites: [] };
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchFlow();
    } else {
      initialFlowRef.current = { nodes: [], sites: [] };
      if (INITIAL_FLOW_FROM_BACKEND?.length) {
        setNodesList(dbFormatToNodesList(INITIAL_FLOW_FROM_BACKEND));
        setColumnAssignments(
          dbFormatToColumnAssignments(INITIAL_FLOW_FROM_BACKEND)
        );
        seedNodeIdMap(nodeIdMapRef.current, INITIAL_FLOW_FROM_BACKEND);
        initialFlowRef.current = nodesListToDbFormat(
          dbFormatToNodesList(INITIAL_FLOW_FROM_BACKEND)
        );
      }
    }
  }, [id, fetchFlow]);

  const handleDeleteNode = useCallback((id: string) => {
    setNodesList((nds) => {
      const nodeToDelete = nds.find((n) => n.id === id);
      if (
        nodeToDelete &&
        "device" in nodeToDelete &&
        nodeToDelete.device &&
        "type" in nodeToDelete
      ) {
        const deviceSnr = nodeToDelete.device.snr;
        const nodeType = nodeToDelete.type;
        if (nodeType === OrgChartNodeType.SOURCE) {
          setColumnAssignments((prev) => ({
            ...prev,
            source: prev.source.filter((s) => s.device.snr !== deviceSnr),
          }));
        } else if (nodeType === OrgChartNodeType.DEVICE) {
          setColumnAssignments((prev) => ({
            ...prev,
            device: prev.device.filter((d) => d.device.snr !== deviceSnr),
          }));
        }
      }
      return nds.filter((node) => node.id !== id);
    });
  }, []);

  const handleDeleteZone = useCallback((id: string) => {
    setColumnAssignments((prev) => ({
      ...prev,
      zone: prev.zone.filter((z) => z.id !== id),
    }));
    setNodesList((prev) =>
      prev.filter((node) => !("leafNodes" in node && node.id === id))
    );
  }, []);

  const handleDeleteEdge = useCallback((source: string, target: string) => {
    setNodesList((nodesList) =>
      nodesList.map((node) => {
        if ("targets" in node && node.id === source) {
          return {
            ...node,
            targets: node.targets.filter((t: string) => t !== target),
          };
        }
        return node;
      })
    );
  }, []);

  useEffect(() => {
    const { nodes: layoutNodes, edges: layoutEdges } = getOrgLayout(
      nodesList,
      direction,
      { width: nodeWidth, height: nodeHeight },
      { width: 50, height: 50 },
      { width: nodeWidth * 2, height: nodeHeight },
      { width: clusterWidth, height: clusterHeight },
      handleDeleteNode,
      handleDeleteEdge,
      handleDeleteZone
    );
    setEdges(layoutEdges);
    setNodes(layoutNodes);
  }, [
    nodesList,
    direction,
    handleDeleteNode,
    handleDeleteEdge,
    handleDeleteZone,
    setEdges,
    setNodes,
  ]);

  useEffect(() => {
    setEdges((eds) =>
      eds.map((edge) => {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        const edgeFromData = sourceNode?.data as
          | SourceNodeData
          | DeviceNodeData
          | DistributionNodeData
          | ZoneNodeData
          | undefined;
        const edgeRed = edgeFromData && isNodeCorrelationError(edgeFromData);
        return {
          ...edge,
          data: {
            ...edge.data,
            onDelete: handleDeleteEdge,
            color: edgeRed ? "#ef4444" : "#000000",
          },
        };
      })
    );
  }, [nodes, handleDeleteEdge, setEdges]);

  // Resize/fit view when nodes change and flow is visible (connection made or Done pressed)
  useEffect(() => {
    if (!showNodePopup && nodes.length > 0 && reactFlowInstanceRef.current) {
      reactFlowInstanceRef.current.fitView({ padding: 0.2, duration: 200 });
    }
  }, [nodes, edges, showNodePopup]);

  const onConnect = useCallback(
    (connection: { source: string; target: string }) => {
      const { source, target } = connection;
      setNodesList((nodesList) =>
        nodesList.map((node) => {
          if (
            "targets" in node &&
            node.id === source &&
            !node.targets.includes(target)
          ) {
            return { ...node, targets: [...node.targets, target] };
          }
          return node;
        })
      );
    },
    []
  );

  const removeDeviceFromZone = useCallback(
    (zoneId: string, deviceSnr: string) => {
      setColumnAssignments((prev) => ({
        ...prev,
        zone: prev.zone.map((z) =>
          z.id === zoneId
            ? {
                ...z,
                devices: z.devices.filter(
                  ({ device }) => device.snr !== deviceSnr
                ),
              }
            : z
        ),
      }));
      setNodesList((prev) =>
        prev.map((node) => {
          if ("leafNodes" in node && node.id === zoneId) {
            return {
              ...node,
              leafNodes: (node as ZoneNodeData).leafNodes.filter(
                (l) => l.device.snr !== deviceSnr
              ),
            };
          }
          return node;
        })
      );
    },
    []
  );

  const fetchDeviceList = useCallback(
    async (siteId?: string, search?: string) => {
      const classId = siteId || selectedSiteId;

      setIsLoadingDevices(true);
      const modelFilter = reticulation?.reticulationType
        ? ModelFilterDropdown.find(
            (m) =>
              m.label.toLowerCase() ===
              reticulation.reticulationType.toLowerCase()
          )
        : null;
      const models =
        modelFilter?.value ?? ModelFilterDropdown.flatMap((m) => m.value);
      const body: DeviceListReqBody = {
        classId,
        sort: "snr",
        desc: true,
        models,
        ...(search?.trim() ? { search: search.trim() } : {}),
      };
      const { data } = await deviceList(body);
      if (data) setSearchDevices(data);
      else setSearchDevices([]);
      setIsLoadingDevices(false);
    },
    [selectedSiteId, reticulation?.reticulationType]
  );

  useEffect(() => {
    fetchDeviceList(selectedSiteId, searchQuery);
  }, [selectedSiteId, searchQuery, fetchDeviceList]);

  // Devices already assigned (source, device column, or inside any zone)
  const usedDeviceSnrs = useMemo(() => {
    const snrs = new Set<string>();
    columnAssignments.source.forEach(({ device }) => snrs.add(device.snr));
    columnAssignments.device.forEach(({ device }) => snrs.add(device.snr));
    columnAssignments.zone.forEach((z) =>
      z.devices.forEach(({ device }) => snrs.add(device.snr))
    );
    return snrs;
  }, [columnAssignments]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      if (!destination) return;

      const srcCol = source.droppableId;
      const destCol = destination.droppableId;
      if (srcCol === destCol && source.index === destination.index) return;

      const isFromSearch = srcCol === COLUMN_IDS.SEARCH;
      const isToSearch = destCol === COLUMN_IDS.SEARCH;
      const ZONE_PREFIX = "zone-";
      const isToZone = destCol.startsWith(ZONE_PREFIX);
      const destZoneId = isToZone ? destCol.slice(ZONE_PREFIX.length) : null;
      const isFromZone = srcCol.startsWith(ZONE_PREFIX);
      const srcZoneId = isFromZone ? srcCol.slice(ZONE_PREFIX.length) : null;

      // Resolve dragged device
      let draggedDevice: Device | null = null;
      if (isFromSearch) {
        draggedDevice = searchDevices[source.index] ?? null;
      } else if (srcCol === COLUMN_IDS.SOURCE) {
        draggedDevice = columnAssignments.source[source.index]?.device ?? null;
      } else if (srcCol === COLUMN_IDS.DEVICE) {
        draggedDevice = columnAssignments.device[source.index]?.device ?? null;
      } else if (isFromZone && srcZoneId) {
        const zone = columnAssignments.zone.find((z) => z.id === srcZoneId);
        draggedDevice = zone?.devices[source.index]?.device ?? null;
      }
      if (!draggedDevice) return;

      // From search → source, device, or zone (never distribution)
      if (isFromSearch && !isToSearch) {
        if (usedDeviceSnrs.has(draggedDevice.snr)) return;
        if (destCol === COLUMN_IDS.DISTRIBUTION) return;
        setColumnAssignments((prev) => {
          if (destCol === COLUMN_IDS.SOURCE) {
            const list = [...prev.source];
            list.splice(destination.index, 0, { device: draggedDevice! });
            return { ...prev, source: list };
          }
          if (destCol === COLUMN_IDS.DEVICE) {
            const list = [...prev.device];
            list.splice(destination.index, 0, { device: draggedDevice! });
            return { ...prev, device: list };
          }
          if (isToZone && destZoneId) {
            return {
              ...prev,
              zone: prev.zone.map((z) =>
                z.id === destZoneId
                  ? {
                      ...z,
                      devices: [
                        ...z.devices.slice(0, destination.index),
                        { device: draggedDevice! },
                        ...z.devices.slice(destination.index),
                      ],
                    }
                  : z
              ),
            };
          }
          return prev;
        });
        return;
      }

      // Move between source/device/zones
      if (!isFromSearch && !isToSearch && destCol !== COLUMN_IDS.DISTRIBUTION) {
        setColumnAssignments((prev) => {
          const remove = (p: ColumnAssignments): ColumnAssignments => {
            if (srcCol === COLUMN_IDS.SOURCE) {
              return {
                ...p,
                source: p.source.filter((_, i) => i !== source.index),
              };
            }
            if (srcCol === COLUMN_IDS.DEVICE) {
              return {
                ...p,
                device: p.device.filter((_, i) => i !== source.index),
              };
            }
            if (isFromZone && srcZoneId) {
              return {
                ...p,
                zone: p.zone.map((z) =>
                  z.id === srcZoneId
                    ? {
                        ...z,
                        devices: z.devices.filter((_, i) => i !== source.index),
                      }
                    : z
                ),
              };
            }
            return p;
          };
          const add = (p: ColumnAssignments): ColumnAssignments => {
            const item = { device: draggedDevice! };
            if (destCol === COLUMN_IDS.SOURCE) {
              const list = [...p.source];
              list.splice(destination.index, 0, item);
              return { ...p, source: list };
            }
            if (destCol === COLUMN_IDS.DEVICE) {
              const list = [...p.device];
              list.splice(destination.index, 0, item);
              return { ...p, device: list };
            }
            if (isToZone && destZoneId) {
              return {
                ...p,
                zone: p.zone.map((z) =>
                  z.id === destZoneId
                    ? {
                        ...z,
                        devices: [
                          ...z.devices.slice(0, destination.index),
                          item,
                          ...z.devices.slice(destination.index),
                        ],
                      }
                    : z
                ),
              };
            }
            return p;
          };
          if (srcCol === destCol && isFromZone && destZoneId === srcZoneId) {
            const zone = prev.zone.find((z) => z.id === srcZoneId)!;
            const list = [...zone.devices];
            const [removed] = list.splice(source.index, 1);
            list.splice(destination.index, 0, removed);
            return {
              ...prev,
              zone: prev.zone.map((z) =>
                z.id === srcZoneId ? { ...z, devices: list } : z
              ),
            };
          }
          return add(remove(prev));
        });
        return;
      }

      // From column → search: unassign
      if (!isFromSearch && isToSearch) {
        setColumnAssignments((prev) => {
          if (srcCol === COLUMN_IDS.SOURCE) {
            return {
              ...prev,
              source: prev.source.filter((_, i) => i !== source.index),
            };
          }
          if (srcCol === COLUMN_IDS.DEVICE) {
            return {
              ...prev,
              device: prev.device.filter((_, i) => i !== source.index),
            };
          }
          if (isFromZone && srcZoneId) {
            return {
              ...prev,
              zone: prev.zone.map((z) =>
                z.id === srcZoneId
                  ? {
                      ...z,
                      devices: z.devices.filter((_, i) => i !== source.index),
                    }
                  : z
              ),
            };
          }
          return prev;
        });
      }
    },
    [searchDevices, columnAssignments, usedDeviceSnrs]
  );

  const buildFlowFromColumns = useCallback(() => {
    const sourceDevices = columnAssignments.source;
    const distributionItems = columnAssignments.distribution;
    const zoneItems = columnAssignments.zone;
    const deviceDevices = columnAssignments.device;

    const logicalKey = (type: string, id: string) => `${type}:${id}`;

    setNodesList((prevNodesList) => {
      const map = nodeIdMapRef.current;
      const currentKeys = new Set<string>();

      // Assign UUID per logical node (reuse if already mapped for same logical key)
      const getId = (key: string) => {
        currentKeys.add(key);
        if (!map.has(key)) map.set(key, crypto.randomUUID());
        return map.get(key)!;
      };

      // Build logicalKey -> UUID for this run
      const logicalToId = new Map<string, string>();
      sourceDevices.forEach(({ device }) => {
        logicalToId.set(
          logicalKey("src", device.snr),
          getId(logicalKey("src", device.snr))
        );
      });
      distributionItems.forEach((d) => {
        logicalToId.set(
          logicalKey("dist", d.id),
          getId(logicalKey("dist", d.id))
        );
      });
      zoneItems.forEach((z) => {
        currentKeys.add(logicalKey("zone", z.id));
        logicalToId.set(logicalKey("zone", z.id), z.id);
      });
      deviceDevices.forEach(({ device }) => {
        logicalToId.set(
          logicalKey("dev", device.snr),
          getId(logicalKey("dev", device.snr))
        );
      });

      // Prune map for nodes no longer in columns
      for (const k of map.keys()) {
        if (!currentKeys.has(k)) map.delete(k);
      }

      const newNodeIds = new Set(logicalToId.values());
      const nodes: Array<
        SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData
      > = [];

      sourceDevices.forEach(({ device }) => {
        const id = logicalToId.get(logicalKey("src", device.snr))!;
        const old = prevNodesList.find((n) => n.id === id);
        const oldTargets = old && "targets" in old ? old.targets : [];
        const targets = oldTargets.filter((t) => newNodeIds.has(t));
        nodes.push(
          new SourceNodeData(
            id,
            targets,
            device.name || device.snr,
            OrgChartNodeType.SOURCE,
            {
              snr: device.snr,
              name: device.name ?? "",
              model: device.model,
              classId: device.classId,
              timezone: device.timezone,
            }
          )
        );
      });

      distributionItems.forEach((d) => {
        const id = logicalToId.get(logicalKey("dist", d.id))!;
        const old = prevNodesList.find((n) => n.id === id);
        const oldTargets = old && "targets" in old ? old.targets : [];
        const targets = oldTargets.filter((t) => newNodeIds.has(t));
        nodes.push(
          new DistributionNodeData(
            id,
            targets,
            d.name,
            OrgChartNodeType.DISTRIBUTION
          )
        );
      });

      zoneItems.forEach((z) => {
        const id = z.id;
        const old = prevNodesList.find((n) => n.id === id);
        const oldZone =
          old && "leafNodes" in old ? (old as ZoneNodeData) : undefined;
        const oldLeavesBySnr = new Map(
          (oldZone?.leafNodes ?? []).map((l) => [l.device.snr, l])
        );
        const leafNodes = z.devices.map(({ device }) => {
          const prevLeaf = oldLeavesBySnr.get(device.snr);
          return {
            label: `${device.name || device.snr}`,
            device: {
              snr: device.snr,
              name: device.name ?? "",
              model: device.model,
              classId: device.classId,
              timezone: device.timezone,
            },
            ...(prevLeaf?.data ? { data: prevLeaf.data } : {}),
          };
        });
        const oldTargets = old && "targets" in old ? old.targets : [];
        const targets = oldTargets.filter((t) => newNodeIds.has(t));
        nodes.push(
          new ZoneNodeData(
            id,
            targets,
            z.name,
            OrgChartNodeType.ZONE,
            leafNodes,
            undefined,
            undefined,
            undefined,
            removeDeviceFromZone,
            oldZone?.data
          )
        );
      });

      deviceDevices.forEach(({ device }) => {
        const id = logicalToId.get(logicalKey("dev", device.snr))!;
        const old = prevNodesList.find((n) => n.id === id);
        const oldTargets = old && "targets" in old ? old.targets : [];
        const targets = oldTargets.filter((t) => newNodeIds.has(t));
        nodes.push(
          new DeviceNodeData(
            id,
            device.name || device.snr,
            OrgChartNodeType.DEVICE,
            {
              snr: device.snr,
              name: device.name ?? "",
              model: device.model,
              classId: device.classId,
              timezone: device.timezone,
            },
            undefined,
            targets
          )
        );
      });

      return nodes;
    });
  }, [columnAssignments, removeDeviceFromZone]);

  const addDistribution = useCallback(() => {
    const id = `dist${genId()}`;
    setColumnAssignments((prev) => ({
      ...prev,
      distribution: [
        ...prev.distribution,
        { id, name: `Distribution ${prev.distribution.length + 1}` },
      ],
    }));
  }, []);

  const addZone = useCallback(() => {
    const id = crypto.randomUUID();
    setColumnAssignments((prev) => ({
      ...prev,
      zone: [
        ...prev.zone,
        { id, name: `Zone ${prev.zone.length + 1}`, devices: [] },
      ],
    }));
  }, []);

  const removeDistribution = useCallback((id: string) => {
    setColumnAssignments((prev) => ({
      ...prev,
      distribution: prev.distribution.filter((d) => d.id !== id),
    }));
  }, []);

  const removeZone = useCallback((id: string) => {
    setColumnAssignments((prev) => ({
      ...prev,
      zone: prev.zone.filter((z) => z.id !== id),
    }));
  }, []);

  const [editingDistId, setEditingDistId] = useState<string | null>(null);
  const [editingZoneId, setEditingZoneId] = useState<string | null>(null);
  const [addingSiteToZoneId, setAddingSiteToZoneId] = useState<string | null>(
    null
  );

  const updateDistributionName = useCallback((id: string, name: string) => {
    setColumnAssignments((prev) => ({
      ...prev,
      distribution: prev.distribution.map((d) =>
        d.id === id ? { ...d, name: name.trim() || d.name } : d
      ),
    }));
    setEditingDistId(null);
  }, []);

  const updateZoneName = useCallback((id: string, name: string) => {
    setColumnAssignments((prev) => ({
      ...prev,
      zone: prev.zone.map((z) =>
        z.id === id ? { ...z, name: name.trim() || z.name } : z
      ),
    }));
    setEditingZoneId(null);
  }, []);

  const addSiteDevicesToZone = useCallback(
    async (zoneId: string) => {
      if (!selectedSiteId) return;
      setAddingSiteToZoneId(zoneId);
      try {
        const body: DeviceListReqBody = {
          classId: selectedSiteId,
          sort: "snr",
          desc: true,
        };
        const { data } = await deviceList(body);
        const siteDevices: Device[] = data ?? [];
        const available = siteDevices.filter((d) => !usedDeviceSnrs.has(d.snr));
        if (available.length > 0) {
          setColumnAssignments((prev) => ({
            ...prev,
            zone: prev.zone.map((z) =>
              z.id === zoneId
                ? {
                    ...z,
                    devices: [
                      ...z.devices,
                      ...available.map((device) => ({ device })),
                    ],
                  }
                : z
            ),
          }));
        }
      } finally {
        setAddingSiteToZoneId(null);
      }
    },
    [selectedSiteId, usedDeviceSnrs]
  );

  const hasAnyAssignments = useMemo(() => {
    return (
      columnAssignments.source.length >= 1 ||
      columnAssignments.distribution.length >= 1 ||
      columnAssignments.zone.length >= 1 ||
      columnAssignments.device.length >= 1
    );
  }, [columnAssignments]);

  const getListClasses = (
    isDraggingOver: boolean,
    columnId: string,
    isDropDisabled: boolean
  ) => {
    const base =
      "min-h-[120px] flex-1 rounded-lg border-2 border-dashed p-2 transition-colors";
    if (isDropDisabled)
      return `${base} border-stone-200 bg-stone-50/50 dark:border-stone-600 dark:bg-stone-800/30`;
    if (isDraggingOver)
      return `${base} border-primary bg-primary/5 dark:bg-primary/10`;
    return `${base} border-stone-200 bg-stone-50 dark:border-stone-600 dark:bg-stone-800/50`;
  };

  const flowNodesForDb = useMemo(
    () => nodesListToDbFormat(nodesList),
    [nodesList]
  );

  const hasUnsavedChanges = useMemo(() => {
    const initial = initialFlowRef.current;
    if (!initial) return false;
    return (
      JSON.stringify(flowNodesForDb.nodes) !== JSON.stringify(initial.nodes) ||
      JSON.stringify(flowNodesForDb.sites) !== JSON.stringify(initial.sites)
    );
  }, [flowNodesForDb, saveRevision]);

  useBackBlocker(() => hasUnsavedChanges);

  const saveFlow = async () => {
    if (id) {
      const body: ReticulationUpdateReqBody = {
        id: id,
        name: reticulation?.name ?? "",
        sites: flowNodesForDb.sites,
        reticulationType: reticulation?.reticulationType ?? "",
        config: flowNodesForDb.nodes,
      };
      dispatch(setLoading(true));
      const { error, status, isLoading } = await updateReticulation(body);
      dispatch(setLoading(isLoading));
      if (status === 200) {
        initialFlowRef.current = flowNodesForDb;
        setSaveRevision((r) => r + 1);
        notifySimple(t("notification.reticulation.update.success"), "success");
      } else {
        notifySimple(error, "error");
      }
    } else {
      const body: ReticulationCreateReqBody = {
        name: reticulation?.name ?? "",
        sites: flowNodesForDb.sites,
        reticulationType: reticulation?.reticulationType ?? "",
        config: flowNodesForDb.nodes,
      };
      dispatch(setLoading(true));
      const { error, status, isLoading } = await addReticulation(body);
      dispatch(setLoading(isLoading));
      if (status === 200) {
        initialFlowRef.current = flowNodesForDb;
        setSaveRevision((r) => r + 1);
        notifySimple(t("notification.reticulation.add.success"), "success");
      } else {
        notifySimple(error, "error");
      }
    }
  };

  return (
    <SecondaryHeightLayout>
      {showNodePopup ? (
        <GenericCard
          title={"Node Editor"}
          description="Drag and drop devices to the columns to create a flow. Quickly find devices by searching or selecting a site. Optionally, you can add distributions and zones to the flow and edit their names and delete them before saving the flow."
          extra="flex h-full min-h-0 flex-col !overflow-visible"
          topRight={
            <div className="flex gap-2">
              <GenericButton
                text="Done"
                onClick={() => {
                  buildFlowFromColumns();
                  setShowNodePopup(false);
                  setFreshKey((prev) => prev + 1);
                }}
                disabled={!hasAnyAssignments}
                color="primary"
                size="sm"
                extra="w-full rounded-lg "
              />
              <CircleButton
                icon={MdClose}
                onClick={() => {
                  setShowNodePopup(false);
                  setTimeout(() => {
                    setFreshKey((prev) => prev + 1);
                  }, 1);
                }}
                color="primary"
                variant="outline"
                size="sm"
                extra="rounded-lg "
              />
            </div>
          }
        >
          <div
            ref={contentContainerRef}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="relative z-20 grid grid-cols-2 gap-2 py-3">
              <SitesCombobox
                filter
                id="site-combobox"
                placeholder={t("common.select.site")}
                variant="solid"
                color="secondary"
                value={selectedSiteId as string}
                onChange={(value) => setSelectedSiteId(value ?? "")}
                sizes="md"
                rounded="rounded-xl"
              />

              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                debounce={500}
                placeholder="Search devices..."
                sizes="md"
              />
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-x-auto overflow-y-hidden">
              <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex min-h-0 flex-1 gap-3 pb-2">
                  {/* Device search column - fixed on left */}
                  <div className="dark:bg-stone-900 sticky left-0 z-10 flex w-[250px] shrink-0 flex-col bg-white">
                    <div className="mb-2 flex shrink-0 items-center gap-2 font-bold text-primary">
                      <MdDragIndicator className="text-lg" />
                      Search results
                    </div>
                    <Droppable droppableId={COLUMN_IDS.SEARCH}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${getListClasses(
                            snapshot.isDraggingOver,
                            COLUMN_IDS.SEARCH,
                            false
                          )}`}
                          {...provided.droppableProps}
                        >
                          {isLoadingDevices ? (
                            <div className="text-stone-500 flex h-full items-center justify-center p-4 text-sm">
                              Loading...
                            </div>
                          ) : searchDevices.length === 0 ? (
                            <div className="text-stone-500 flex h-full flex-col items-center justify-center p-4 text-center text-sm">
                              {selectedSiteId
                                ? "No devices found"
                                : "Select a site to search"}
                            </div>
                          ) : (
                            searchDevices.map((device, index) => {
                              const isUsed = usedDeviceSnrs.has(device.snr);
                              return (
                                <Draggable
                                  key={`search-${device.snr}`}
                                  draggableId={`search-${device.snr}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`mb-2 flex cursor-grab flex-col gap-0.5 rounded-lg border px-3 py-2 transition-colors active:cursor-grabbing ${
                                        isUsed
                                          ? "border-stone-200 bg-stone-100 dark:border-stone-600 dark:bg-stone-700/50 opacity-60"
                                          : "border-stone-200 bg-stone-50 hover:bg-stone-100 dark:border-stone-600 dark:bg-stone-700/50 dark:hover:bg-stone-700"
                                      } ${
                                        snapshot.isDragging ? "shadow-md" : ""
                                      }`}
                                    >
                                      <span className="truncate text-sm font-medium text-primary">
                                        {device.name || device.snr}
                                      </span>
                                      <span className="truncate text-xs text-secondary">
                                        SNR: {device.snr}
                                        {device.model
                                          ? ` · ${device.model}`
                                          : ""}
                                      </span>
                                      {isUsed && (
                                        <span className="mt-1 text-xs text-amber-600">
                                          Already assigned
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>

                  {/* Source column */}
                  <div className="flex w-[250px] shrink-0 flex-col">
                    <div className="mb-2 flex shrink-0 items-center justify-between font-bold text-primary">
                      <span className="flex items-center gap-2">
                        <MdDragIndicator className="text-lg" />
                        Source
                      </span>
                    </div>
                    <Droppable droppableId={COLUMN_IDS.SOURCE}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${getListClasses(
                            snapshot.isDraggingOver,
                            "source",
                            false
                          )}`}
                          {...provided.droppableProps}
                        >
                          {columnAssignments.source.map(({ device }, index) => (
                            <AssignedDeviceCard
                              key={`source-${device.snr}`}
                              draggableId={`source-${device.snr}`}
                              index={index}
                              device={device}
                              onRemove={() =>
                                setColumnAssignments((prev) => ({
                                  ...prev,
                                  source: prev.source.filter(
                                    (s) => s.device.snr !== device.snr
                                  ),
                                }))
                              }
                            />
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>

                  {/* Distribution column - Add only, no device drops */}
                  {/* <div className="flex w-[250px] shrink-0 flex-col">
                    <div className="mb-2 flex shrink-0 items-center justify-between font-bold text-primary">
                      <span className="flex items-center gap-2">
                        <MdDragIndicator className="text-lg" />
                        Distribution
                      </span>
                      <CircleButton
                        icon={MdAdd}
                        onClick={addDistribution}
                        size="xs"
                        variant="outline"
                        color="secondary"
                        extra="rounded-lg "
                        tooltip="Add distribution"
                      />
                    </div>
                    <div
                      className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${getListClasses(
                        false,
                        "distribution",
                        false
                      )}`}
                    >
                      {columnAssignments.distribution.map((d) => (
                        <div
                          key={d.id}
                          className="border-stone-200 dark:border-stone-600 dark:bg-stone-800 mb-2 flex items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2"
                        >
                          {editingDistId === d.id ? (
                            <input
                              type="text"
                              value={d.name}
                              onChange={(e) =>
                                setColumnAssignments((prev) => ({
                                  ...prev,
                                  distribution: prev.distribution.map((dist) =>
                                    dist.id === d.id
                                      ? { ...dist, name: e.target.value }
                                      : dist
                                  ),
                                }))
                              }
                              onBlur={() =>
                                updateDistributionName(d.id, d.name)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  updateDistributionName(d.id, d.name);
                                }
                                if (e.key === "Escape") {
                                  setEditingDistId(null);
                                }
                              }}
                              autoFocus
                              className="border-stone-300 dark:border-stone-600 dark:bg-stone-800 min-w-0 flex-1 rounded border bg-white px-1.5 py-0.5 text-sm text-primary focus:border-primary focus:outline-none"
                            />
                          ) : (
                            <span
                              onClick={() => setEditingDistId(d.id)}
                              className="min-w-0 flex-1 cursor-pointer truncate text-sm font-medium text-primary hover:underline"
                            >
                              {d.name}
                            </span>
                          )}
                          <CircleButton
                            icon={MdClose}
                            onClick={() => removeDistribution(d.id)}
                            size="xs"
                            variant="ghost"
                            tooltip="Remove distribution"
                          />
                        </div>
                      ))}
                    </div>
                  </div> */}

                  {/* Zone column - Add zone first, then drag devices into each zone */}
                  <div className="flex w-[250px] shrink-0 flex-col">
                    <div className="mb-2 flex shrink-0 items-center justify-between font-bold text-primary">
                      <span className="flex items-center gap-2">
                        <MdDragIndicator className="text-lg" />
                        Zone
                      </span>
                      <CircleButton
                        icon={MdAdd}
                        onClick={addZone}
                        size="xs"
                        variant="outline"
                        color="secondary"
                        extra="rounded-lg "
                        tooltip="Add zone"
                      />
                    </div>
                    <div className="border-stone-200 bg-stone-50 dark:border-stone-600 dark:bg-stone-800/50 min-h-0 flex-1 space-y-3 overflow-y-auto overflow-x-hidden rounded-lg border-2 border-dashed p-2">
                      {columnAssignments.zone.map((z) => (
                        <div
                          key={z.id}
                          className="border-stone-200 bg-stone-50/50 dark:border-stone-600 dark:bg-stone-800/50 flex flex-col rounded-lg border"
                        >
                          <div className="border-stone-200 dark:border-stone-600 flex items-center justify-between gap-2 border-b px-2 py-1.5">
                            {editingZoneId === z.id ? (
                              <input
                                type="text"
                                value={z.name}
                                onChange={(e) =>
                                  setColumnAssignments((prev) => ({
                                    ...prev,
                                    zone: prev.zone.map((zone) =>
                                      zone.id === z.id
                                        ? { ...zone, name: e.target.value }
                                        : zone
                                    ),
                                  }))
                                }
                                onBlur={() => updateZoneName(z.id, z.name)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    updateZoneName(z.id, z.name);
                                  }
                                  if (e.key === "Escape") {
                                    setEditingZoneId(null);
                                  }
                                }}
                                autoFocus
                                className="border-stone-300 dark:border-stone-600 dark:bg-stone-800 min-w-0 flex-1 rounded border bg-white px-1.5 py-0.5 text-sm text-primary focus:border-primary focus:outline-none"
                              />
                            ) : (
                              <span
                                onClick={() => setEditingZoneId(z.id)}
                                className="min-w-0 flex-1 cursor-pointer truncate text-sm font-medium text-primary hover:underline"
                              >
                                {z.name}
                              </span>
                            )}
                            <div className="flex shrink-0 items-center gap-1">
                              <CircleButton
                                icon={MdLocationOn}
                                onClick={() => addSiteDevicesToZone(z.id)}
                                size="xs"
                                variant="outline"
                                color="secondary"
                                extra="rounded-lg "
                                disabled={
                                  !selectedSiteId || addingSiteToZoneId === z.id
                                }
                                tooltip={
                                  selectedSiteId
                                    ? "Add all devices from selected site"
                                    : "Select a site first"
                                }
                              />

                              <CircleButton
                                icon={MdClose}
                                onClick={() => removeZone(z.id)}
                                size="xs"
                                variant="ghost"
                                tooltip="Remove zone"
                              />
                            </div>
                          </div>
                          <Droppable droppableId={`zone-${z.id}`}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                className={`max-h-[200px] min-h-[60px] overflow-y-auto p-2 ${
                                  snapshot.isDraggingOver ? "bg-primary/5" : ""
                                }`}
                                {...provided.droppableProps}
                              >
                                {z.devices.map(({ device }, index) => (
                                  <AssignedDeviceCard
                                    key={`zone-${z.id}-${device.snr}`}
                                    draggableId={`zone-${z.id}-${device.snr}`}
                                    index={index}
                                    device={device}
                                    onRemove={() =>
                                      setColumnAssignments((prev) => ({
                                        ...prev,
                                        zone: prev.zone.map((zone) =>
                                          zone.id === z.id
                                            ? {
                                                ...zone,
                                                devices: zone.devices.filter(
                                                  (d) =>
                                                    d.device.snr !== device.snr
                                                ),
                                              }
                                            : zone
                                        ),
                                      }))
                                    }
                                  />
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Device column */}
                  <div className="flex w-[250px] shrink-0 flex-col">
                    <div className="mb-2 flex shrink-0 items-center gap-2 font-bold text-primary">
                      <MdDragIndicator className="text-lg" />
                      Device
                    </div>
                    <Droppable droppableId={COLUMN_IDS.DEVICE}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden ${getListClasses(
                            snapshot.isDraggingOver,
                            "device",
                            false
                          )}`}
                          {...provided.droppableProps}
                        >
                          {columnAssignments.device.map(({ device }, index) => (
                            <AssignedDeviceCard
                              key={`device-${device.snr}`}
                              draggableId={`device-${device.snr}`}
                              index={index}
                              device={device}
                              onRemove={() =>
                                setColumnAssignments((prev) => ({
                                  ...prev,
                                  device: prev.device.filter(
                                    (d) => d.device.snr !== device.snr
                                  ),
                                }))
                              }
                            />
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              </DragDropContext>
            </div>
          </div>
        </GenericCard>
      ) : (
        <>
          <div className="w-full" ref={searchPanelRef}>
            <div className="flex w-full justify-between rounded-[20px] border bg-white px-5 py-4">
              <div className="flex items-center gap-2">
                <div className="text-lg font-bold text-primary">
                  {reticulation?.name} - {reticulation?.reticulationType}
                </div>
                <GenericButton
                  text="Nodes"
                  onClick={() => setShowNodePopup(!showNodePopup)}
                  color="primary"
                  size="md"
                  variant="outline"
                  extra="rounded-lg"
                />
              </div>
              <div className="flex gap-2">
                <CircleButton
                  onClick={() =>
                    setDirection((d) => (d === "LR" ? "TB" : "LR"))
                  }
                  icon={
                    direction === "LR"
                      ? MdAlignVerticalTop
                      : MdAlignHorizontalLeft
                  }
                  tooltip={
                    direction === "LR" ? "Vertical layout" : "Horizontal layout"
                  }
                  color="primary"
                  variant="outline"
                  size="md"
                  extra="rounded-lg "
                />

                <GenericButton
                  text="Save"
                  onClick={saveFlow}
                  color="primary"
                  size="md"
                  extra="w-full rounded-lg"
                  disabled={!hasAnyAssignments}
                />
              </div>
            </div>
          </div>
          <div
            key={freshKey}
            className="h-full w-full"
            style={{
              height:
                dimensions.contentHeight -
                searchPanelRef.current?.clientHeight -
                DOCUMENT_PADDING,
              marginTop: DOCUMENT_PADDING,
            }}
          >
            <GenericCard extra="flex h-full min-h-0 flex-col !overflow-visible">
              <div className="h-full min-h-0 flex-1">
                <ReactFlow
                  className="h-full w-full"
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onInit={(instance) => {
                    reactFlowInstanceRef.current = instance;
                  }}
                  connectionLineType={ConnectionLineType.SmoothStep}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  minZoom={0}
                  maxZoom={2}
                  fitView
                >
                  <Controls />
                  <Background />
                </ReactFlow>
              </div>
            </GenericCard>
          </div>
        </>
      )}
    </SecondaryHeightLayout>
  );
};

export default Flow;
